import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { PositionStatus, POSITION_STATUS_LABELS } from '../types';

/* ============================ 岗位优先级 ============================ */
export const PRIORITY_OPTIONS: { value: string; label: string; short: string; color: string }[] = [
  { value: '', label: '无', short: '无', color: 'border-dashed border-gray-300 text-gray-400' },
  { value: 'P0', label: 'P0 紧急', short: '高', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'P1', label: 'P1 重要', short: '中', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'P2', label: 'P2 一般', short: '低', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'reserve', label: '长期储备', short: '储备', color: 'bg-gray-100 text-gray-600 border-gray-300' },
];

export function priorityMeta(value?: string | null) {
  return PRIORITY_OPTIONS.find(o => o.value === (value || '')) || PRIORITY_OPTIONS[0];
}

export function PriorityBadge({ value }: { value?: string | null }) {
  if (!value) return <span className="text-xs text-gray-300">—</span>;
  const meta = priorityMeta(value);
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-bold border', meta.color)}>
      {meta.label}
    </span>
  );
}

/* ============================ 岗位状态 ============================ */
const STATUS_COLORS: Record<PositionStatus, string> = {
  draft: 'bg-gray-100 text-gray-500',
  open: 'bg-green-100 text-green-700',
  paused: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-100 text-gray-400',
};

export function PositionStatusBadge({ status }: { status: PositionStatus }) {
  return (
    <span className={clsx('px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap', STATUS_COLORS[status] || STATUS_COLORS.draft)}>
      {POSITION_STATUS_LABELS[status] || status}
    </span>
  );
}

/** 头像：无图时用名字首字取色，与候选人列表的观感一致 */
export function Avatar({ name, size = 24 }: { name?: string | null; size?: number }) {
  const palette = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-sky-500', 'bg-violet-500'];
  const label = (name || '?').trim().slice(0, 1).toUpperCase();
  const idx = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  return (
    <span className={clsx('rounded-full text-white flex items-center justify-center font-medium shrink-0', palette[idx])}
      style={{ width: size, height: size, fontSize: size * 0.45 }}>
      {label}
    </span>
  );
}

export const fmtDate = (v?: string | null) => (v ? String(v).slice(0, 10) : '—');
export const fmtDateTime = (v?: string | null) => (v ? String(v).slice(0, 16) : '—');

/* ============================ 部门选项 ============================ */
export interface DeptOption {
  id: string;
  name: string;
  full_name: string;
  parent_name: string | null;
  level?: number;      // 0=一级(留形总部) 1=二级(研发部) 2=三级(软件组)
  root_name?: string | null;
}

/** 部门下拉数据源（org_units 的活跃部门），含「新建部门」能力 */
export function useDepartments(enabled = true) {
  const [options, setOptions] = useState<DeptOption[]>([]);

  const reload = useCallback(() => {
    fetch('/api/org/departments', { headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` } })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setOptions(data); })
      .catch(() => {});
  }, []);

  useEffect(() => { if (enabled) reload(); }, [enabled, reload]);

  /** 创建部门，名称格式「父部门-子部门」时挂到父部门下 */
  const createDepartment = useCallback(async (fullName: string) => {
    const parts = fullName.trim().split('-');
    const name = parts.length > 1 ? parts[parts.length - 1] : parts[0];
    const parentName = parts.length > 1 ? parts[0] : '';
    const parent = parentName ? options.find(d => d.full_name === parentName || d.full_name.endsWith(parentName)) : null;
    await fetch('/api/org/units', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
      body: JSON.stringify({ name, type: 'team', parent_id: parent?.id || null }),
    });
    reload();
  }, [options, reload]);

  return { options, reload, createDepartment };
}

/* ============================ 可搜索下拉 ============================ */
export function SearchableSelect({ value, options, placeholder, emptyLabel, searchPlaceholder, onChange, onAdd, addLabel, className }: {
  value: string;
  options: { label: string; value: string }[];
  placeholder?: string;
  emptyLabel?: string;
  searchPlaceholder?: string;
  onChange: (v: string) => void;
  onAdd?: () => void;
  addLabel?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => { if (open) { setQuery(''); inputRef.current?.focus(); } }, [open]);

  const displayLabel = options.find(o => o.value === value)?.label || value || placeholder || '选择...';

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 text-left truncate flex items-center justify-between gap-1">
        <span className={clsx('truncate', !value && 'text-gray-400')}>{displayLabel}</span>
        <ChevronRight size={12} className={clsx('shrink-0 text-gray-400 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 flex flex-col">
          <div className="p-1.5 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder || '搜索...'}
                className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              className={clsx('w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50', !value && 'bg-primary-50 text-primary-600')}>
              {emptyLabel || '未分配'}
            </button>
            {filtered.map(o => (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
                className={clsx('w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 truncate', value === o.value && 'bg-primary-50 text-primary-600 font-medium')}>
                {o.label}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">无匹配结果</div>}
          </div>
          {onAdd && (
            <button type="button" onClick={() => { setOpen(false); onAdd(); }}
              className="w-full text-left px-3 py-1.5 text-xs text-primary-600 hover:bg-primary-50 border-t border-gray-100 font-medium">
              {addLabel || '+ 新建...'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** 二级部门下拉：只展示二级部门（如 研发部/市场部），以花名册飞书部门为准。创建与编辑抽屉共用 */
export function DepartmentSelect({ value, onChange, className, deptHook }: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  deptHook: ReturnType<typeof useDepartments>;
}) {
  const { options, createDepartment } = deptHook;
  // level===1 即二级部门；老库无 level 字段时回退展示全部（兼容）
  const secondary = options.some(d => d.level !== undefined)
    ? options.filter(d => d.level === 1)
    : options;
  return (
    <SearchableSelect
      value={value}
      options={[
        ...secondary.map(d => ({ label: d.name, value: d.name })),
        // 存量岗位上的部门文本可能与当前花名册不一致，保留为可选项避免保存时被清空
        ...(value && !secondary.find(d => d.name === value) ? [{ label: `${value}（未录入）`, value }] : []),
      ]}
      placeholder="选择二级部门"
      searchPlaceholder="搜索部门..."
      onChange={onChange}
      onAdd={() => {
        const name = window.prompt('输入新部门名称（如需创建子部门，格式：父部门-子部门）');
        if (name?.trim()) createDepartment(name.trim()).then(() => onChange(name.trim()));
      }}
      addLabel="+ 新建部门..."
      className={className}
    />
  );
}

/** 三级部门下拉：联动所选二级部门，展示其下三级部门（如 研发部→软件组/算法组）。可选、不强制 */
export function SubDepartmentSelect({ department, value, onChange, className, deptHook }: {
  department: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  deptHook: ReturnType<typeof useDepartments>;
}) {
  const { options } = deptHook;
  // level===2 且父部门为所选二级部门
  const tertiary = options.filter(d => d.level === 2 && d.parent_name === department);
  const disabled = !department;
  return (
    <SearchableSelect
      value={value}
      options={[
        ...tertiary.map(d => ({ label: d.name, value: d.name })),
        ...(value && !tertiary.find(d => d.name === value) ? [{ label: `${value}（未录入）`, value }] : []),
      ]}
      placeholder={disabled ? '请先选择二级部门' : (tertiary.length ? '选择三级部门（可选）' : '该部门下暂无三级部门')}
      emptyLabel="不指定三级部门"
      searchPlaceholder="搜索三级部门..."
      onChange={onChange}
      className={className}
    />
  );
}
